import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import {
  Card, Alert, StatsRow, StatCard,
} from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { RefreshCw, Download, Search } from "lucide-react";
import { toast } from "sonner";

const fmtEur = (cents) =>
  `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n) => Number(n || 0).toLocaleString("it-IT");

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS_BADGE = {
  completed: { bg: "bg-success-subtle", color: "text-success-strong", label: "Completata" },
  pending:   { bg: "bg-warning-subtle", color: "text-warning-strong", label: "In attesa" },
  processing:{ bg: "bg-accent-subtle",  color: "text-accent-strong",  label: "In corso" },
  failed:    { bg: "bg-danger-subtle",  color: "text-danger-strong",  label: "Fallita" },
};

const Badge = ({ status }) => {
  const def = STATUS_BADGE[status] || { bg: "bg-subtle", color: "text-text-secondary", label: status || "—" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${def.bg} ${def.color}`}>
      {def.label}
    </span>
  );
};

export default function SuperAdminRequests() {
  const [rows, setRows] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState("all");
  const [serviceId, setServiceId] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const [failed, setFailed] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (orgId !== "all") params.org_id = orgId;
      if (serviceId !== "all") params.service_id = serviceId;
      if (status !== "all") params.status = status;
      const { data } = await api.get("/superadmin/requests", { params });
      setRows(data);
      setFailed(false);
    } catch { setFailed(true); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [o, s] = await Promise.all([
          api.get("/superadmin/organizations"),
          api.get("/admin/services"),
        ]);
        setOrgs(o.data);
        setServices(s.data);
      } catch {}
    })();
  }, []);

  useEffect(() => { load(); }, [orgId, serviceId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.input_summary || "").toLowerCase().includes(q) ||
      (r.org_id || "").toLowerCase().includes(q) ||
      (r.service_id || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const kpis = useMemo(() => {
    const totalCost = filtered.reduce((s, r) => s + (r.cost_cents || 0), 0);
    const completed = filtered.filter((r) => r.status === "completed").length;
    const cacheHits = filtered.filter((r) => r.cache_hit).length;
    return { total: filtered.length, totalCost, completed, cacheHits };
  }, [filtered]);

  const orgsById = useMemo(() => {
    const m = {}; orgs.forEach((o) => m[o.id] = o.name); return m;
  }, [orgs]);
  const servicesById = useMemo(() => {
    const m = {}; services.forEach((s) => m[s.service_id] = s.name); return m;
  }, [services]);

  const exportCsv = () => {
    if (!filtered.length) return;
    const rows = [
      ["Data", "Org", "Servizio", "Status", "Input", "Cache", "Costo (€)", "Quota"],
      ...filtered.map((r) => [
        r.created_at, orgsById[r.org_id] || r.org_id, servicesById[r.service_id] || r.service_id,
        r.status, r.input_summary || "", r.cache_hit ? "SI" : "NO",
        ((r.cost_cents || 0) / 100).toFixed(4),
        r.counted_in_quota ? "SI" : "NO",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tutte-richieste.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">Wattline · Super admin</div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Tutte le richieste</h1>
          <p className="text-base text-text-secondary mt-2">Vista globale di tutte le chiamate OpenAPI cross-org</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="h-10 rounded-md border-border" data-testid="sa-req-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Aggiorna
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={!filtered.length}
                  className="h-10 rounded-md border-border" data-testid="sa-req-export">
            <Download size={14} className="mr-1.5" /> Esporta CSV
          </Button>
        </div>
      </div>

      <StatsRow columns={4}>
        <StatCard label="Richieste" value={fmtInt(kpis.total)} detail="Nel filtro corrente" testId="sa-req-kpi-total" />
        <StatCard label="Completate" value={fmtInt(kpis.completed)}
                  detail={`${kpis.total ? Math.round((kpis.completed / kpis.total) * 100) : 0}%`}
                  detailColor="success" testId="sa-req-kpi-completed" />
        <StatCard label="Cache hits" value={fmtInt(kpis.cacheHits)}
                  detail="Costi evitati" detailColor="success" testId="sa-req-kpi-cache" />
        <StatCard label="Costo totale" value={fmtEur(kpis.totalCost)} detail="Spesa OpenAPI" testId="sa-req-kpi-cost" />
      </StatsRow>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 max-w-md relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Cerca per input, org, servizio…"
                 className="pl-9 h-10 rounded-md border-border bg-surface" data-testid="sa-req-search" />
        </div>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="w-48 h-10 rounded-md border-border bg-surface" data-testid="sa-req-org-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le org.</SelectItem>
            {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={serviceId} onValueChange={setServiceId}>
          <SelectTrigger className="w-56 h-10 rounded-md border-border bg-surface" data-testid="sa-req-svc-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i servizi</SelectItem>
            {services.map((s) => <SelectItem key={s.service_id} value={s.service_id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-10 rounded-md border-border bg-surface" data-testid="sa-req-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="completed">Completate</SelectItem>
            <SelectItem value="processing">In corso</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="failed">Fallite</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-surface border border-border rounded-xl overflow-hidden" data-testid="sa-req-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-text-secondary text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Data</th>
                <th className="text-left px-4 py-3 font-medium">Organizzazione</th>
                <th className="text-left px-4 py-3 font-medium">Servizio</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Input</th>
                <th className="text-right px-4 py-3 font-medium">Costo</th>
                <th className="text-center px-4 py-3 font-medium">Cache</th>
                <th className="text-center px-4 py-3 font-medium">Quota</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-muted">Caricamento…</td></tr>
              ) : failed ? (
                <tr><td colSpan={8}><LoadErrorBox what="le richieste" onRetry={load} testId="sa-requests-error" /></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-text-muted">Nessuna richiesta trovata</td></tr>
              ) : filtered.map((r) => (
                <tr key={r.id} className="border-t border-border hover:bg-subtle" data-testid={`sa-req-row-${r.id}`}>
                  <td className="px-4 py-2.5 text-xs text-text-secondary tabular-nums whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <Link to={`/superadmin/organizations/${r.org_id}`} className="text-accent hover:underline">
                      {orgsById[r.org_id] || r.org_id}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 text-text-primary">{servicesById[r.service_id] || r.service_id}</td>
                  <td className="px-4 py-2.5"><Badge status={r.status} /></td>
                  <td className="px-4 py-2.5 text-text-secondary text-xs max-w-xs truncate" title={r.input_summary}>{r.input_summary || "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-text-primary">{fmtEur(r.cost_cents)}</td>
                  <td className="px-4 py-2.5 text-center">
                    {r.cache_hit ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] bg-success-subtle text-success-strong font-medium">HIT</span> : <span className="text-text-muted text-xs">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs">
                    {r.counted_in_quota ? <span className="text-text-secondary">SI</span> : <span className="text-text-muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length >= 500 && (
          <div className="px-4 py-2.5 bg-subtle border-t border-border text-xs text-text-muted text-center">
            Visualizzati 500 risultati (massimo per pagina). Restringi i filtri per vedere risultati più mirati.
          </div>
        )}
      </div>
    </div>
  );
}
